import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';
import { acquireAccessToken, isAuthEnabled, requireReauthentication, authDebugLog } from './authService';

export class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = API_BASE_URL) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        const url = config.url || '';
        const authEnabled = isAuthEnabled();
        authDebugLog(`[Interceptor] Request to: ${url} (Auth Enabled: ${authEnabled})`);

        const attachHeader = (key: string, value: string) => {
          if (!config.headers) {
            config.headers = {} as any;
          }

          // Support Axios 1.x Headers object if present
          if (typeof (config.headers as any).set === 'function') {
            (config.headers as any).set(key, value);
          } else {
            (config.headers as any)[key] = value;
          }
        };

        const correlationId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        attachHeader('X-Correlation-Id', correlationId);

        if (authEnabled) {
          try {
            const token = await acquireAccessToken();
            if (token) {
              attachHeader('Authorization', `Bearer ${token}`);
              authDebugLog(`[Interceptor] Attached MSAL token to request: ${url}`);
            } else {
              authDebugLog(`[Interceptor] NO token returned from acquireAccessToken for: ${url}`);
            }
          } catch (error) {
            authDebugLog(`[Interceptor] Exception acquiring token for: ${url}`, error);
          }
        } else {
          const token = localStorage.getItem('authToken');
          if (token) {
            attachHeader('Authorization', `Bearer ${token}`);
            authDebugLog(`[Interceptor] Attached legacy token for: ${url}`);
          } else {
            authDebugLog(`[Interceptor] Auth disabled and NO legacy token found for: ${url}`);
          }
        }

        const timezone = Intl?.DateTimeFormat?.().resolvedOptions().timeZone;
        if (timezone) {
          attachHeader('X-User-Timezone', timezone);
        }

        authDebugLog(`[Interceptor] Final Headers for ${url}:`, {
          hasAuth: !!((config.headers as any).Authorization || (config.headers as any).get?.('Authorization')),
          timezone: (config.headers as any)['X-User-Timezone'] || (config.headers as any).get?.('X-User-Timezone'),
          correlationId: (config.headers as any)['X-Correlation-Id'] || (config.headers as any).get?.('X-Correlation-Id')
        });

        return config;
      },
      (error) => {
        authDebugLog('[Interceptor] Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        authDebugLog(`[Interceptor] SUCCESS from: ${response.config.url}`);
        return response;
      },
      async (error) => {
        const status = error.response?.status;
        const url = error.config?.url;

        authDebugLog(`[Interceptor] ERROR ${status} from: ${url}`, {
          message: error.message,
          data: error.response?.data
        });

        if (status === 401) {
          authDebugLog('[Interceptor] 401 Unauthorized - attempting silent token refresh before re-auth');
          localStorage.removeItem('authToken');
          if (isAuthEnabled()) {
            // Avoid retrying the same request more than once to prevent an infinite loop.
            if (!error.config._retried) {
              error.config._retried = true;
              // Try to get a fresh token silently without prompting user popup
              const freshToken = await acquireAccessToken(true, false);
              if (freshToken) {
                // Retry the original request with the new token.
                error.config.headers['Authorization'] = `Bearer ${freshToken}`;
                return this.client.request(error.config);
              }
              // No token available — surface a login popup once.
              await requireReauthentication();
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  getBaseURL(): string {
    return this.client.defaults.baseURL || '';
  }
}

export const apiClient = new ApiClient();
export default apiClient;
